import type { ScopeSearchTreeNode } from '$lib/types/installationScopeTree';
import type { AnyScopeNode } from '$lib/types/installationScopeTree';
import type { components } from '../../api-types';

type NodeChild = components['schemas']['NodeChild'];

const SCOPE_CHILDREN_KEYS: (keyof ScopeSearchTreeNode)[] = [
  'chains',
  'subChains',
  'regions',
  'locations',
  'installations'
];

function hasAnyChildren(node: ScopeSearchTreeNode): boolean {
  for (const key of SCOPE_CHILDREN_KEYS) {
    const children = node[key];
    if (Array.isArray(children) && children.length > 0) return true;
  }
  return false;
}

export function extractPathsFromScopeTree(root: ScopeSearchTreeNode): string[][] {
  if (!hasAnyChildren(root)) return [];

  const paths: string[][] = [];
  const walk = (node: ScopeSearchTreeNode, prefix: string[]) => {
    const currentPath = [...prefix, node.id];
    let hasChildren = false;
    for (const key of SCOPE_CHILDREN_KEYS) {
      const children = node[key];
      if (Array.isArray(children) && children.length > 0) {
        hasChildren = true;
        for (const child of children as ScopeSearchTreeNode[]) {
          walk(child, currentPath);
        }
      }
    }
    if (!hasChildren) {
      paths.push(currentPath);
    }
  };
  walk(root, []);
  return paths;
}

export function buildSearchChildrenIndex(root: AnyScopeNode): Map<string, NodeChild[]> {
  const index = new Map<string, NodeChild[]>();

  const walk = (node: AnyScopeNode, prefix: string[]) => {
    const currentPath = [...prefix, node.id];
    const children: AnyScopeNode[] = 'children' in node ? (node.children ?? []) : [];
    if (children.length > 0) {
      index.set(
        currentPath.join('|'),
        children.map((child) => ({
          id: child.id,
          name: child.name,
          installationCount: child.installationCount,
          manifests: child.manifests.map((m) => ({ id: m.id, name: m.name }))
        }))
      );
      for (const child of children) {
        walk(child, currentPath);
      }
    }
  };

  walk(root, []);
  return index;
}
